import React, { useState, useRef, useCallback, useEffect } from 'react';

interface UseChatInputProps {
  tableId?: string;
}

interface UseChatInputReturn {
  inputMessage: string;
  setInputMessage: (value: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  panelInputRef: React.RefObject<HTMLTextAreaElement>;
  adjustTextareaHeight: (textarea: HTMLTextAreaElement | null) => void;
  handleKeyPress: (e: React.KeyboardEvent, onSend: () => void) => void;
  clearInput: () => void;
}

/**
 * Hook for managing chat input state and textarea behavior
 */
export function useChatInput({ tableId }: UseChatInputProps = {}): UseChatInputReturn {
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const panelInputRef = useRef<HTMLTextAreaElement>(null);
  const prevTableIdRef = useRef<string | undefined>(tableId);

  // Reset input state when tableId changes
  useEffect(() => {
    if (prevTableIdRef.current !== tableId) {
      prevTableIdRef.current = tableId;
      setInputMessage('');
      setIsLoading(false);
    }
  }, [tableId]);

  /**
   * Auto-resize textarea based on content
   * Min height: 44px, Max height: 140px
   */
  const adjustTextareaHeight = useCallback((textarea: HTMLTextAreaElement | null) => {
    if (textarea) {
      textarea.style.height = 'auto';
      const minHeight = 44;
      const maxHeight = 140;
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  /**
   * Handle Enter key press - send on Enter, newline on Shift+Enter
   * Ignores Enter during IME composition (for CJK input)
   */
  const handleKeyPress = useCallback((e: React.KeyboardEvent, onSend: () => void) => {
    // Don't send during IME composition (CJK input)
    if (e.nativeEvent.isComposing) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  }, []);

  const clearInput = useCallback(() => {
    setInputMessage('');
  }, []);

  return {
    inputMessage,
    setInputMessage,
    isLoading,
    setIsLoading,
    inputRef,
    panelInputRef,
    adjustTextareaHeight,
    handleKeyPress,
    clearInput,
  };
}
